import { useRef } from "react"
import { Button, Center, Drawer, Loader, Text, Group, CloseButton } from "@mantine/core"
import { Trans, useTranslation } from "react-i18next"
import { modals } from "@mantine/modals"
import { useAgencyUser, useDeleteAgencyUser } from "../hooks/useAgencyUsers"
import { useUser } from "../../user/hooks/useUser"
import AgencyUserDetailContent from "./AgencyUserDetailContent"
import AgencyUserFormContent from "./AgencyUserFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedUserId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function AgencyUserDrawer({ opened, onClose, mode, selectedUserId, onDeleted, onSwitchToEdit }: Props) {
  const { t } = useTranslation()
  const { data: user, isLoading } = useAgencyUser(mode !== "create" ? selectedUserId : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencyUsers ?? false
  const deleteMutation = useDeleteAgencyUser()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t("agencyUsers.confirmDelete"),
      children: <Text size="sm">{t("agencyUsers.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(user!.id, {
          onSuccess: () => {
            onDeleted()
          },
        })
      },
    })
  }

  const title =
    mode === "create"
      ? t("agencyUsers.createTitle")
      : mode === "edit"
        ? t("agencyUsers.editTitle")
        : (user?.full_name ?? "")

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md">{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <AgencyUserFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !user ? (
        <Text>
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <AgencyUserFormContent ref={formRef} user={user} onSuccess={onClose} />
      ) : (
        <AgencyUserDetailContent user={user} />
      )}
    </Drawer>
  )
}
